/**
 * Composant de barre de force de mot de passe
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Affiche une barre de progression visuelle de la force du mot de passe
 * avec suggestions d'amélioration.
 */

  // Client Component pour Next.js

import type { PasswordStrengthDetails } from '../../../application/dto/ui.types';

/**
 * Props du composant PasswordStrengthMeter
 */
export interface PasswordStrengthMeterProps {
  /** Détails de la force du mot de passe */
  strength: PasswordStrengthDetails;
  /** Afficher les suggestions d'amélioration */
  showSuggestions?: boolean;
  /** Afficher les critères détaillés */
  showCriteria?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Map des couleurs selon le niveau de force
 */
const STRENGTH_COLORS = {
  weak: 'bg-red-500',
  fair: 'bg-orange-500',
  good: 'bg-yellow-500',
  strong: 'bg-lime-500',
  'very-strong': 'bg-green-600',
} as const;

/**
 * Map des labels en français
 */
const STRENGTH_LABELS = {
  weak: 'Très faible',
  fair: 'Faible',
  good: 'Moyen',
  strong: 'Fort',
  'very-strong': 'Très fort',
} as const;

/**
 * Composant PasswordStrengthMeter
 *
 * @example
 * ```tsx
 * function PasswordInput() {
 *   const [password, setPassword] = useState('');
 *   const strength = usePasswordStrength(password);
 *
 *   return (
 *     <div>
 *       <input
 *         type="password"
 *         value={password}
 *         onChange={(e) => setPassword(e.target.value)}
 *       />
 *       <PasswordStrengthMeter strength={strength} />
 *     </div>
 *   );
 * }
 * ```
 */
export function PasswordStrengthMeter({
  strength,
  showSuggestions = true,
  showCriteria = true,
  className = '',
}: PasswordStrengthMeterProps) {
  const widthPercentage = (strength.score / 5) * 100;
  const colorClass = STRENGTH_COLORS[strength.label];
  const labelText = STRENGTH_LABELS[strength.label];

  return (
    <div className={`w-full ${className}`} role="region" aria-label="Force du mot de passe">
      {/* Barre de progression */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-content-muted">
            Force du mot de passe
          </span>
          <span
            className={`text-sm font-semibold ${
              strength.score <= 1
                ? 'text-red-600'
                : strength.score === 2
                ? 'text-orange-600'
                : strength.score === 3
                ? 'text-yellow-600'
                : strength.score === 4
                ? 'text-lime-600'
                : 'text-green-600'
            }`}
            aria-live="polite"
          >
            {labelText}
          </span>
        </div>

        {/* Barre */}
        <div className="w-full h-2 bg-surface-overlay rounded-full overflow-hidden">
          <div
            className={`h-full ${colorClass} transition-all duration-300 ease-out`}
            style={{ width: `${widthPercentage}%` }}
            role="progressbar"
            aria-valuenow={strength.score}
            aria-valuemin={0}
            aria-valuemax={5}
            aria-label={`Force: ${labelText}`}
          />
        </div>
      </div>

      {/* Critères détaillés */}
      {showCriteria && (
        <div className="mb-2 space-y-1">
          <CriterionItem
            label="Au moins 8 caractères"
            met={strength.hasMinLength}
          />
          <CriterionItem
            label="Contient une majuscule"
            met={strength.hasUppercase}
          />
          <CriterionItem
            label="Contient une minuscule"
            met={strength.hasLowercase}
          />
          <CriterionItem
            label="Contient un chiffre"
            met={strength.hasNumber}
          />
          <CriterionItem
            label="Contient un caractère spécial"
            met={strength.hasSpecialChar}
          />
        </div>
      )}

      {/* Suggestions d'amélioration */}
      {showSuggestions && strength.suggestions.length > 0 && (
        <div className="mt-3" role="alert" aria-live="polite">
          <p className="text-xs font-medium text-content-muted mb-1">
            Suggestions :
          </p>
          <ul className="space-y-1">
            {strength.suggestions.map((suggestion, index) => (
              <li
                key={index}
                className="text-xs text-content-muted flex items-start"
              >
                <span className="mr-1.5 mt-0.5 text-orange-500" aria-hidden="true">
                  •
                </span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Composant pour afficher un critère individuel
 */
interface CriterionItemProps {
  label: string;
  met: boolean;
}

function CriterionItem({ label, met }: CriterionItemProps) {
  return (
    <div className="flex items-center text-xs">
      <span
        className={`mr-2 ${met ? 'text-success' : 'text-content-subtle'}`}
        aria-hidden="true"
      >
        {met ? '✓' : '○'}
      </span>
      <span
        className={met ? 'text-content' : 'text-content-subtle'}
      >
        {label}
      </span>
    </div>
  );
}
